import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useQuestions from '@/hooks/useQuestions';
import { ApplicationAnswerProps, ApplicationProps } from '@/models/Application';
import { ApplicationTemplateQuestionProps } from '@/models/ApplicationTemplate';
import { ProgramNames } from '@/models/Program';
import { QuestionProps, SignatureQuestionResponse } from '@/models/Question';
import { QuestionReviewProps } from '@/models/QuestionReview';
import { StandardQuestionDetails } from '@/models/Standard';
import { Locales, UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { showChildQuestion } from '@/utils/questionUtil';
import { LocaleContext } from './LocaleContext';
import { UsersContext } from './UsersContext';
import { EventProps } from '@/models/Event';
import useApplication from '@/hooks/useApplication';
import { SelectedProgramContext } from './SelectedProgramContext';
import { ContactProps } from '@/models/Contact';
import useContact from '@/hooks/useContact';
import useSignature from '@/hooks/useSignature';
import { PartnerProps } from '@/models/Partner';
import { PartnerGroupProps } from '@/models/PartnerGroup';
import usePartner from '@/hooks/usePartner';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import useApplicationCycle from '@/hooks/useApplicationCycle';
import { Spinner } from '@/components/common/Spinner';
import { logError } from '@/utils/errorHandlerUtil';
import { Star, StarStatus } from '@/models/Star';
import { isTreeCityWorldGrowthAwardApplication } from '@/utils/applicationUtil';
import { ApplicationTypes } from '@/models/ApplicationType';
import usePreviewAnswer from '@/hooks/usePreviewAnswer';

interface StandardErrorDetails {
  standardId: number;
  status: boolean;
  count: number;
  total: number;
}

interface Props {
  isPreviewMode: boolean;
  canEdit: boolean;
  currentApplication: ApplicationProps;
  currentStandardId: number;
  currentStandardInfo: StandardQuestionDetails;
  sortedStandards: StandardQuestionDetails[];
  isFirstStandard: boolean;
  isLastStandard: boolean;
  standardQuestionIds: number[] | null;
  allRequiredQuestions: { [key: number]: unknown } | null;
  questions: ApplicationTemplateQuestionProps;
  includedInTotalQuestion: boolean | null;
  standardError: { [key: number]: boolean };
  answers: Partial<Record<number, ApplicationAnswerProps>>;
  allComments: QuestionReviewProps[] | null;
  makingApiRequest: boolean;
  star: Star | null;
  saveApplicationAnswer: (
    applicationId: number,
    questionId: number,
    answer: string | number | Array<string | number> | null,
    dataType?: string,
    locale?: Locales
  ) => Promise<ApplicationAnswerProps | null>;
  saveApplicationAnswer2: (
    question: QuestionProps,
    answer: string | number | Array<string | number> | null
  ) => Promise<ApplicationAnswerProps | null>;
  updateCurrentApplication: (newData: ApplicationProps | null) => void;
  updateCurrentStandardId: (newStandard: number) => void;
  updateCurrentStandardInfo: (newStandardInfo: StandardQuestionDetails) => void;
  updateAnswers: (answer: ApplicationAnswerProps) => void;
  updateMakingApiRequest: (q: boolean) => void;
  updateIncludedInTotalQuestion: (q: boolean) => void;
  allEvents: EventProps[] | null | undefined;
  updateCurrentEvents: (events?: EventProps[]) => void;
  currentContacts: ContactProps[] | null;
  updateContacts: (newContacts: ContactProps[] | null) => void;
  updateCurrentContacts: (newContacts: ContactProps[] | null) => void;
  updateApplicationContactRelationships: () => Promise<void>;
  signatures: SignatureQuestionResponse[] | null;
  updateSignatures: (s: SignatureQuestionResponse[]) => void;
  applicationLoading: boolean;
  partnerId: number | null;
  partnerGroupId: number | null;
  partnerInfo: PartnerProps | null;
  partnerGroupInfo: PartnerGroupProps | null;
  applicationCycle: ApplicationCycle | null;
}

interface ProviderProps {
  appid?: string;
  previewCycleId?: number;
  children: React.ReactNode;
}

type AnswerSaveResult = Promise<ApplicationAnswerProps | null>;

type QueuedAnswerSave = {
  payloadKey: string;
  save: () => AnswerSaveResult;
  promise: AnswerSaveResult;
  resolve: (value: ApplicationAnswerProps | null) => void;
  reject: (reason?: unknown) => void;
};

type AnswerSaveQueueEntry = {
  activePayloadKey: string;
  activePromise: AnswerSaveResult;
  queuedSave: QueuedAnswerSave | null;
};

export const CurrentApplicationContext = createContext<Props>({
  isPreviewMode: false,
  canEdit: false,
  currentApplication: {} as ApplicationProps,
  currentStandardInfo: {} as StandardQuestionDetails,
  sortedStandards: [],
  currentStandardId: -1,
  isFirstStandard: true,
  isLastStandard: false,
  standardQuestionIds: null,
  allRequiredQuestions: null,
  questions: {} as ApplicationTemplateQuestionProps,
  includedInTotalQuestion: null,
  standardError: {},
  answers: {},
  allComments: null,
  makingApiRequest: false,
  star: null,
  saveApplicationAnswer: () => Promise.resolve({} as ApplicationAnswerProps),
  saveApplicationAnswer2: () => Promise.resolve({} as ApplicationAnswerProps),
  updateCurrentApplication: () => {},
  updateCurrentStandardId: () => {},
  updateCurrentStandardInfo: () => {},
  updateAnswers: () => {},
  updateMakingApiRequest: () => {},
  updateIncludedInTotalQuestion: () => {},
  allEvents: undefined,
  updateCurrentEvents: () => {},
  currentContacts: [] as ContactProps[],
  updateContacts: () => {},
  updateCurrentContacts: () => {},
  updateApplicationContactRelationships: () => Promise.resolve(),
  signatures: null,
  updateSignatures: () => {},
  applicationLoading: false,
  partnerId: null,
  partnerGroupId: null,
  partnerInfo: null,
  partnerGroupInfo: null,
  applicationCycle: null,
});

function computeCanEdit(
  application: ApplicationProps,
  userType: UserType | null,
  currentStandardId: number | null
): boolean {
  if (application.status == null) return false;
  if (userType !== UserType.Adf && !application.applicationCycleActive) return false;

  switch (userType) {
    case UserType.Applicant: {
      const isEditableStatus = [
        ApplicationStatus.InProgress,
        ApplicationStatus.InRevision,
      ].includes(application.status);

      if (!isEditableStatus) return false;

      const shouldLockAwardedGrowthAward =
        application.programId === ProgramNames.TreeCitiesWorld &&
        application.applicationTypeId === ApplicationTypes.GrowthAward &&
        application.status === ApplicationStatus.InRevision &&
        isTreeCityWorldGrowthAwardApplication(application);

      if (!shouldLockAwardedGrowthAward) return true;

      const currentStandardStar = application.stars.find(
        (s) => s.standardId === currentStandardId && s.applicationId === application.id
      );

      return currentStandardStar?.starStatusId !== StarStatus.Awarded;
    }
    case UserType.RegionalCoordinator:
      return (
        [
          ApplicationStatus.InProgress,
          ApplicationStatus.PendingRegionalReview,
          ApplicationStatus.InRevision,
        ].includes(application.status) &&
        [
          ProgramNames.TreeCityUSA,
          ProgramNames.TreeLineUSA,
          ProgramNames.TreeCampusHigherEducation,
        ].includes(application.programId)
      );
    case UserType.StateCoordinator:
      return (
        application.status !== null &&
        application.status <= ApplicationStatus.InRevision &&
        [
          ProgramNames.TreeCityUSA,
          ProgramNames.TreeLineUSA,
          ProgramNames.TreeCampusHigherEducation,
        ].includes(application.programId)
      );
    case UserType.NationalProgrammeLead:
      return (
        application.status !== null &&
        application.programId === ProgramNames.TreeCitiesWorld &&
        [
          ApplicationStatus.InProgress,
          ApplicationStatus.InRevision,
          ApplicationStatus.PendingNationalReview,
        ].includes(application.status)
      );
    case UserType.Adf:
      return application.status !== ApplicationStatus.Closed;
    default:
      return false;
  }
}

function getStandardSortOrder(standard: StandardQuestionDetails): number {
  return standard.order ?? Number.MAX_SAFE_INTEGER;
}

function computeAllRequiredQuestions(
  standards: StandardQuestionDetails[]
): { [key: number]: unknown } | null {
  const newObj: { [key: number]: unknown } = {};
  standards.forEach((standard) => {
    standard.questions.forEach((q) => {
      if (q.questionNeeded) newObj[q.id] = null;
    });
  });
  return Object.keys(newObj).length > 0 ? newObj : null;
}

function computeStandardError(
  standards: StandardQuestionDetails[],
  answers: { [key: number]: ApplicationAnswerProps },
  applicationStatus: number | undefined
): { [key: number]: boolean } {
  const propertiesToCheck: (keyof ApplicationAnswerProps)[] = [
    'choices',
    'files',
    'responseDate',
    'responseDecimal',
    'responseFile',
    'responseInt',
    'responseString',
    'responseSignature',
    'responseString_foreign',
  ];

  const answerHasData = (answer: ApplicationAnswerProps): boolean => {
    if (answer == null) return false;
    return propertiesToCheck.some((property) => {
      const value = answer[property];
      if (answer.questionType === 'event' && Array.isArray(value)) return false;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    });
  };

  const checkStandardComplete = (ids: number[], standardId: number): StandardErrorDetails => {
    const filtered = Object.values<ApplicationAnswerProps>(answers).filter((a) =>
      ids.includes(a.questionId)
    );
    const arr = filtered.map(answerHasData);
    return {
      standardId,
      status: arr.length > 0 ? arr.every(Boolean) : false,
      count: arr.filter(Boolean).length,
      total: arr.length,
    };
  };

  const result: { [key: number]: boolean } = {};
  standards.forEach((standard) => {
    const ids: number[] = [];
    standard.questions
      .filter((q) => q.questionNeeded)
      .forEach((question) => {
        if (question.parentQuestion != null && question.parentQuestion.length > 0) {
          if (showChildQuestion(question, answers)) ids.push(question.id);
        } else {
          ids.push(question.id);
        }
      });

    if (ids.length > 0) {
      const se = checkStandardComplete(ids, standard.standardId);
      result[se.standardId] = se.status;
    } else if (
      applicationStatus != null &&
      ![ApplicationStatus.InProgress, ApplicationStatus.InRevision].includes(applicationStatus)
    ) {
      result[standard.standardId] = true;
    }
  });

  return result;
}

export const CurrentApplicationProvider = ({ appid, previewCycleId, children }: ProviderProps) => {
  const { userType } = useContext(UsersContext);
  const { locale, localeLanguageCode } = useContext(LocaleContext);
  const { updateSelectedProgramId } = useContext(SelectedProgramContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { postAnswer, getQuestionAnswers, getQuestions, getEvents } = useQuestions();
  const { getOneApplication } = useApplication();
  const { getAllContactsByApplicationId } = useContact();
  const { getSignatures } = useSignature();
  const { getPartner, getPartnerGroup } = usePartner();
  const { getApplicationCycle } = useApplicationCycle();

  const [fetchedApplication, setFetchedApplication] = useState<ApplicationProps | null>(null);
  const [fetchedAnswers, setFetchedAnswers] = useState<{
    [key: number]: ApplicationAnswerProps;
  } | null>(null);
  const [fetchedQuestions, setFetchedQuestions] = useState<ApplicationTemplateQuestionProps | null>(
    null
  );
  const [standardError, setStandardError] = useState<{ [key: number]: boolean }>({});
  const [allRequiredQuestions, setAllRequiredQuestions] = useState<{
    [key: number]: unknown;
  } | null>(null);
  const answerSaveQueueRef = useRef<Map<string, AnswerSaveQueueEntry>>(new Map());
  const [fetchedCurrentStandardId, setFetchedCurrentStandardId] = useState<number | null>(null);
  const [fetchedCurrentStandardInfo, setFetchedCurrentStandardInfo] =
    useState<StandardQuestionDetails | null>(null);
  const [makingApiRequest, setMakingApiRequest] = useState<boolean>(false);
  const [includedInTotalQuestion, setIncludedInTotalQuestion] = useState<boolean>(false);
  const [allEvents, setAllEvents] = useState<EventProps[] | undefined | null>(null);
  const [currentContacts, setCurrentContacts] = useState<ContactProps[] | null>(null);
  const [signatures, setSignatures] = useState<SignatureQuestionResponse[] | null>(null);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [partnerGroupId, setPartnerGroupId] = useState<number | null>(null);
  const [partnerInfo, setPartnerInfo] = useState<PartnerProps | null>(null);
  const [partnerGroupInfo, setPartnerGroupInfo] = useState<PartnerGroupProps | null>(null);
  const [applicationCycle, setApplicationCycle] = useState<ApplicationCycle | null>(null);

  // Ref avoids a fetchingSignatures state variable that would cause extra re-renders
  const fetchingSignaturesRef = useRef(false);
  // Track the last ?standard= param value this effect wrote, so back/forward navigation
  // (URL changes we didn't cause) are recognised as external and honoured.
  const lastWrittenStandardParamRef = useRef<string | null>(null);

  // Derived values — computed inline, no state or effects needed
  const isPreviewMode = previewCycleId != null;
  const canEdit = isPreviewMode
    ? true
    : fetchedApplication != null
      ? computeCanEdit(fetchedApplication, userType, fetchedCurrentStandardId)
      : false;
  const sortedStandards = useMemo(() => {
    if (fetchedQuestions?.standards == null) return [];

    return [...fetchedQuestions.standards].sort((a, b) => {
      const orderDiff = getStandardSortOrder(a) - getStandardSortOrder(b);
      if (orderDiff !== 0) return orderDiff;

      return a.standardId - b.standardId;
    });
  }, [fetchedQuestions]);
  const currentStandardIndex = useMemo(() => {
    if (fetchedCurrentStandardId == null || sortedStandards.length === 0) return -1;

    return sortedStandards.findIndex(
      (standard) => standard.standardId === fetchedCurrentStandardId
    );
  }, [fetchedCurrentStandardId, sortedStandards]);

  const isFirstStandard = currentStandardIndex <= 0;
  const isLastStandard =
    currentStandardIndex >= 0 && currentStandardIndex === sortedStandards.length - 1;

  const resolvedCurrentStandardInfo = useMemo(() => {
    if (fetchedCurrentStandardId == null) return fetchedCurrentStandardInfo;

    return (
      sortedStandards.find((standard) => standard.standardId === fetchedCurrentStandardId) ??
      fetchedCurrentStandardInfo
    );
  }, [fetchedCurrentStandardId, fetchedCurrentStandardInfo, sortedStandards]);

  const requestedInitialStandardIndex = useMemo(() => {
    const standardParam = searchParams.get('standard');
    if (standardParam == null) return null;

    const parsed = Number(standardParam);
    return Number.isInteger(parsed) && parsed >= 1 ? parsed - 1 : null;
  }, [searchParams]);
  const standardQuestionIds = (fetchedCurrentStandardInfo?.questions ?? []).map(
    (q: QuestionProps) => q.id
  );
  const allComments: QuestionReviewProps[] | null =
    fetchedAnswers != null
      ? Object.values(fetchedAnswers)
          .map((r: ApplicationAnswerProps) => r.reviews)
          .filter((reviews) => reviews != null)
          .flat()
      : null;

  useEffect(() => {
    if (sortedStandards.length === 0) return;

    const currentStandardParam = searchParams.get('standard');

    // If the URL ?standard= changed to something we didn't write, it's browser
    // back/forward — honour the URL instead of re-asserting fetchedCurrentStandardId.
    const isExternalNavigation =
      fetchedCurrentStandardId != null &&
      currentStandardParam != null &&
      currentStandardParam !== lastWrittenStandardParamRef.current;

    const currentStandard =
      !isExternalNavigation && fetchedCurrentStandardId != null
        ? sortedStandards.find((standard) => standard.standardId === fetchedCurrentStandardId)
        : null;

    const requestedStandard =
      requestedInitialStandardIndex != null ? sortedStandards[requestedInitialStandardIndex] : null;

    const resolvedStandard = currentStandard ?? requestedStandard ?? sortedStandards[0];
    if (resolvedStandard == null) return;

    if (fetchedCurrentStandardId !== resolvedStandard.standardId) {
      setFetchedCurrentStandardId(resolvedStandard.standardId);
    }

    // Locale changes can reload standards with the same ID but updated translated text.
    // Update current standard info whenever the resolved standard object instance changes.
    if (fetchedCurrentStandardInfo !== resolvedStandard) {
      setFetchedCurrentStandardInfo(resolvedStandard);
    }

    const resolvedStandardIndex = sortedStandards.findIndex(
      (standard) => standard.standardId === resolvedStandard.standardId
    );
    if (resolvedStandardIndex < 0) return;

    const nextStandardParam = String(resolvedStandardIndex + 1);
    // replace on first write only (initial URL sync); push for real navigations
    const shouldReplace = lastWrittenStandardParamRef.current === null;
    lastWrittenStandardParamRef.current = nextStandardParam;

    if (currentStandardParam === nextStandardParam) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('standard', nextStandardParam);
    setSearchParams(nextSearchParams, { replace: shouldReplace });
  }, [
    sortedStandards,
    fetchedCurrentStandardId,
    requestedInitialStandardIndex,
    fetchedCurrentStandardInfo,
    searchParams,
    setSearchParams,
  ]);

  // Load all application data once when appid is available
  useEffect(() => {
    if (isPreviewMode) return;
    if (appid == null) return;

    const load = async () => {
      try {
        const applicationData = await getOneApplication(appid);

        if (applicationData == null) {
          navigate('/error');
          return;
        }

        updateSelectedProgramId(applicationData.programId);

        setPartnerId(applicationData.partnerId);
        const part = await getPartner(applicationData.partnerId);
        setPartnerInfo(part);

        setPartnerGroupId(part.partnerGroupId!);
        const pg = await getPartnerGroup(part.partnerGroupId!);
        setPartnerGroupInfo(pg);

        const cts = await getAllContactsByApplicationId(applicationData.id);
        const dedupedContacts = cts.filter(
          (contact, index, self) => index === self.findIndex((c) => c.id === contact.id)
        );

        setCurrentContacts(dedupedContacts);

        const fetchedEvents = await getEvents(applicationData.id);
        if (fetchedEvents != null) setAllEvents(fetchedEvents as EventProps[]);

        if (!fetchingSignaturesRef.current) {
          fetchingSignaturesRef.current = true;
          const s = await getSignatures(applicationData.id);
          fetchingSignaturesRef.current = false;
          setSignatures(s ?? []);
        }

        const cycle = getApplicationCycle(applicationData.applicationCycleId);
        if (cycle != null) setApplicationCycle(cycle);

        setFetchedApplication(applicationData);
      } catch (e: unknown) {
        logError(
          `Error loading application data for application ${appid}: ${e instanceof Error ? e.message : String(e)}`
        );
        navigate('/error');
      }
    };

    void load();
  }, [
    appid,
    getAllContactsByApplicationId,
    getApplicationCycle,
    getEvents,
    getOneApplication,
    getPartner,
    getPartnerGroup,
    getSignatures,
    navigate,
    isPreviewMode,
    updateSelectedProgramId,
  ]);

  // Load (or reload) questions and answers when locale changes.
  useEffect(() => {
    if (isPreviewMode) return;
    if (fetchedApplication?.id == null || localeLanguageCode == null) return;

    const load = async () => {
      const questionResponse = await getQuestions(
        fetchedApplication.applicationTemplateId,
        localeLanguageCode
      );
      if (questionResponse == null) return;

      const answersResponse = await getQuestionAnswers(fetchedApplication.id, locale);

      // Validate questions
      questionResponse.standards?.forEach((standard) => {
        standard.questions.forEach((question) => {
          if (question.questionText?.trim() === '' || question.questionText == null) {
            logError(`Question with ID ${question.id} is missing question text.`);
          }

          if (question.questionChoices != null && question.questionChoices.length > 0) {
            question.questionChoices.forEach((choice) => {
              if (choice.choice?.trim() === '' || choice.choice == null) {
                logError(
                  `Choice with ID ${choice.choiceId} for question ID ${question.id} is missing choice text.`
                );
              }
            });

            question.questionChoices.sort((a, b) => a.choiceOrder - b.choiceOrder);
          }
        });
      });

      const answerDict: { [key: number]: ApplicationAnswerProps } = {};
      answersResponse.forEach((answer) => {
        answerDict[answer.questionId] = answer;
      });

      const stdError = computeStandardError(
        questionResponse.standards,
        answerDict,
        fetchedApplication.status
      );

      setStandardError(stdError);
      setFetchedAnswers(answerDict);
      setAllRequiredQuestions(computeAllRequiredQuestions(questionResponse.standards ?? []));
      setFetchedQuestions(questionResponse);
    };

    // TODO(bug): load() has no try/catch — if getQuestionAnswers (which doesn't catch, unlike
    // getQuestions) rejects, this is an unhandled rejection and the provider stays on the Spinner
    // forever. There is also no cancellation flag, so on a rapid locale switch an older response
    // can overwrite the newer one. Expected: catch -> logError + navigate('/error'), and ignore
    // stale responses via a `cancelled` flag in the effect cleanup.
    void load();
  }, [
    fetchedApplication?.applicationTemplateId,
    fetchedApplication?.id,
    fetchedApplication?.status,
    getQuestionAnswers,
    getQuestions,
    isPreviewMode,
    locale,
    localeLanguageCode,
  ]);

  useEffect(() => {
    if (!isPreviewMode) return;
    if (previewCycleId == null || localeLanguageCode == null) return;

    const cycle = getApplicationCycle(previewCycleId);
    if (cycle == null) {
      navigate('/error');
      return;
    }

    const previewApplication: ApplicationProps = {
      applicationContactId: -1,
      applicationCycle: cycle.applicationCycleName,
      applicationCycleId: cycle.id,
      applicationCycleActive: true,
      id: -previewCycleId,
      applicationTypeId: cycle.applicationTypeId,
      applicationTemplateId: cycle.applicationTemplateId,
      applicationType: {} as never,
      recognitionYear: cycle.year,
      partner: 'Preview Partner',
      partnerId: -1,
      programId: cycle.programId,
      shippingContactId: null,
      status: ApplicationStatus.InProgress,
      eligibleReviewerContacts: [],
      specialInstructions: null,
      additionalStickers: null,
    };

    const loadPreview = async () => {
      const questionResponse = await getQuestions(cycle.applicationTemplateId, localeLanguageCode);
      if (questionResponse == null) {
        navigate('/error');
        return;
      }

      setFetchedApplication(previewApplication);
      setFetchedAnswers({});
      setFetchedQuestions(questionResponse);
      setStandardError(
        computeStandardError(questionResponse.standards, {}, previewApplication.status)
      );
      setAllRequiredQuestions(computeAllRequiredQuestions(questionResponse.standards ?? []));
      setCurrentContacts([]);
      setSignatures([]);
      setAllEvents([]);
      setApplicationCycle(cycle);
      setPartnerId(null);
      setPartnerGroupId(null);
      setPartnerInfo(null);
      setPartnerGroupInfo(null);
      updateSelectedProgramId(cycle.programId);
    };

    void loadPreview();
  }, [
    getApplicationCycle,
    getQuestions,
    isPreviewMode,
    localeLanguageCode,
    navigate,
    previewCycleId,
    updateSelectedProgramId,
  ]);

  const star: Star | null =
    fetchedApplication == null || !isTreeCityWorldGrowthAwardApplication(fetchedApplication)
      ? null
      : (fetchedApplication.stars.find(
          (s: Star) =>
            s.applicationId === fetchedApplication.id && s.standardId === fetchedCurrentStandardId
        ) ?? null);

  const updateAnswers = (answer: ApplicationAnswerProps) => {
    setFetchedAnswers((prev) => {
      const updated = { ...prev, [answer.questionId]: answer };
      if (fetchedQuestions?.standards != null) {
        setStandardError(
          computeStandardError(fetchedQuestions.standards, updated, fetchedApplication?.status)
        );
      }
      return updated;
    });
  };

  const { savePreviewAnswerByQuestionId, savePreviewAnswerByQuestion } = usePreviewAnswer({
    isPreviewMode,
    fetchedAnswers,
    fetchedQuestions,
    fetchedApplicationId: fetchedApplication?.id,
    updateAnswers,
  });

  const updateApplicationContactRelationships = useCallback(async () => {
    if (isPreviewMode) return;
    if (fetchedApplication === null) return;

    const contacts = await getAllContactsByApplicationId(fetchedApplication.id);
    setCurrentContacts(contacts);
  }, [fetchedApplication, getAllContactsByApplicationId, isPreviewMode]);

  const queueAnswerSave = useCallback(
    (applicationId: number, questionId: number, payload: unknown, save: () => AnswerSaveResult) => {
      const queueKey = `${applicationId}:${questionId}`;
      const payloadKey = JSON.stringify(payload);
      const existing = answerSaveQueueRef.current.get(queueKey);

      if (existing == null) {
        const entry: AnswerSaveQueueEntry = {
          activePayloadKey: payloadKey,
          activePromise: Promise.resolve(null),
          queuedSave: null,
        };

        const run = (
          nextPayloadKey: string,
          nextSave: () => AnswerSaveResult
        ): AnswerSaveResult => {
          const promise = Promise.resolve().then(nextSave);
          entry.activePayloadKey = nextPayloadKey;
          entry.activePromise = promise;

          void promise.then(
            () => {
              const queued = entry.queuedSave;
              if (queued == null) {
                answerSaveQueueRef.current.delete(queueKey);
                return;
              }

              entry.queuedSave = null;
              run(queued.payloadKey, queued.save).then(queued.resolve, queued.reject);
            },
            () => {
              const queued = entry.queuedSave;
              if (queued == null) {
                answerSaveQueueRef.current.delete(queueKey);
                return;
              }

              entry.queuedSave = null;
              run(queued.payloadKey, queued.save).then(queued.resolve, queued.reject);
            }
          );

          return promise;
        };

        answerSaveQueueRef.current.set(queueKey, entry);
        return run(payloadKey, save);
      }

      // TODO(bug): if a different payload is already queued (A in flight, B queued) and the user
      // reverts to A, this returns the active promise but leaves B queued, so B is written last and
      // the server ends up with a value the user abandoned. Expected: a request matching the active
      // payload should cancel/replace the queued save (resolve its callers with A's result) or be
      // queued behind it. Repro: CurrentApplicationContext.test.tsx "A -> B -> A" (skipped).
      if (existing.activePayloadKey === payloadKey) return existing.activePromise;
      if (existing.queuedSave?.payloadKey === payloadKey) return existing.queuedSave.promise;

      if (existing.queuedSave != null) {
        existing.queuedSave.payloadKey = payloadKey;
        existing.queuedSave.save = save;
        return existing.queuedSave.promise;
      }

      let resolveQueuedSave: (value: ApplicationAnswerProps | null) => void = () => {};
      let rejectQueuedSave: (reason?: unknown) => void = () => {};
      const queuedPromise = new Promise<ApplicationAnswerProps | null>((resolve, reject) => {
        resolveQueuedSave = resolve;
        rejectQueuedSave = reject;
      });

      existing.queuedSave = {
        payloadKey,
        save,
        promise: queuedPromise,
        resolve: resolveQueuedSave,
        reject: rejectQueuedSave,
      };

      return queuedPromise;
    },
    []
  );

  const saveApplicationAnswer = async (
    applicationId: number,
    questionId: number,
    answer: string | number | Array<string | number> | null,
    dataType?: string,
    answerLocale?: Locales
  ): Promise<ApplicationAnswerProps | null> => {
    if (isPreviewMode) {
      return savePreviewAnswerByQuestionId(questionId, answer, dataType);
    }

    return queueAnswerSave(
      applicationId,
      questionId,
      { answer, dataType, answerLocale },
      async () => {
        setMakingApiRequest(true);
        try {
          const updatedAnswers = await postAnswer(
            applicationId,
            questionId,
            answer,
            dataType,
            undefined,
            answerLocale
          );
          if (updatedAnswers != null) updateAnswers(updatedAnswers);
          return updatedAnswers;
        } finally {
          setMakingApiRequest(false);
        }
      }
    );
  };

  const saveApplicationAnswer2 = async (
    question: QuestionProps,
    answer: string | number | Array<string | number> | null
  ): Promise<ApplicationAnswerProps | null> => {
    if (isPreviewMode) {
      return savePreviewAnswerByQuestion(question, answer);
    }

    if (fetchedApplication == null)
      throw Error("Can't save an application answer because the application isn't loaded.");
    if (locale == null)
      throw Error("Can't save an application answer because there is not locale.");

    return queueAnswerSave(
      fetchedApplication.id,
      question.id,
      { answer, dataType: question.dataType, questionType: question.questionType, locale },
      async () => {
        setMakingApiRequest(true);
        try {
          const updatedAnswers = await postAnswer(
            fetchedApplication.id,
            question.id,
            answer,
            question.dataType,
            question.questionType,
            locale
          );
          if (updatedAnswers != null) updateAnswers(updatedAnswers);
          return updatedAnswers;
        } finally {
          setMakingApiRequest(false);
        }
      }
    );
  };

  const applicationLoading =
    fetchedApplication == null ||
    fetchedQuestions == null ||
    fetchedAnswers == null ||
    fetchedCurrentStandardId == null ||
    resolvedCurrentStandardInfo == null;

  const answers = fetchedAnswers!;
  const currentApplication = fetchedApplication!;
  const questions = fetchedQuestions!;
  const currentStandardId = fetchedCurrentStandardId!;
  const currentStandardInfo = resolvedCurrentStandardInfo!;

  return (
    <CurrentApplicationContext.Provider
      value={{
        isPreviewMode,
        canEdit,
        currentApplication,
        currentStandardId,
        currentStandardInfo,
        sortedStandards,
        isFirstStandard,
        isLastStandard,
        standardQuestionIds,
        allRequiredQuestions,
        questions,
        includedInTotalQuestion,
        standardError,
        answers,
        allComments,
        makingApiRequest,
        star,
        saveApplicationAnswer,
        saveApplicationAnswer2,
        updateCurrentApplication: setFetchedApplication,
        updateCurrentStandardId: setFetchedCurrentStandardId,
        updateCurrentStandardInfo: setFetchedCurrentStandardInfo,
        updateAnswers,
        updateMakingApiRequest: setMakingApiRequest,
        updateIncludedInTotalQuestion: setIncludedInTotalQuestion,
        allEvents,
        updateCurrentEvents: setAllEvents,
        currentContacts,
        updateContacts: setCurrentContacts,
        updateCurrentContacts: (x?: ContactProps[] | null) => setCurrentContacts(x ?? []),
        updateApplicationContactRelationships,
        signatures,
        updateSignatures: setSignatures,
        applicationLoading,
        partnerId,
        partnerGroupId,
        partnerInfo,
        partnerGroupInfo,
        applicationCycle,
      }}
    >
      {applicationLoading ? <Spinner /> : children}
    </CurrentApplicationContext.Provider>
  );
};
